import { useState } from 'react';

import { isNullOrWhitespace } from '@/utils/stringHelper';
import { UserLanguage } from '@/utils/translation';
import { DEFAULT_CURRENCY, PriceMultiplierByCurrency } from '@/utils/price';

import {
  defaultNewContainerType,
  type NewContainerPrice,
} from '../newContainerType';
import {
  deepClone,
  DRAFT_ID,
  type ContainerTypeDraft,
} from './containerTypeAdminMapping';

type UseContainerTypeDraftParams = {
  language: string;
  groupOptions: { value: string; label: string }[];
};

/** Multiply the EUR prices into another currency (as the old add modal did). */
function expandDraftPrices(
  base: NewContainerPrice,
  currency: string,
): NewContainerPrice {
  const multiplier = (PriceMultiplierByCurrency as any)[currency] ?? 1;
  const expanded: any = {};
  for (const attr of Object.keys(base)) {
    expanded[attr] = String(
      Math.floor(parseFloat((base as any)[attr] || '0') * multiplier * 1000) /
        1000,
    );
  }
  return expanded as NewContainerPrice;
}

/**
 * State of the "New container type" draft (replaces AddContainerTypeModal): a
 * draft edited in the same sheet as real container types. The create
 * orchestration lives in useContainerTypesAdmin.
 */
export function useContainerTypeDraft({
  language,
  groupOptions,
}: UseContainerTypeDraftParams) {
  const [draft, setDraft] = useState<ContainerTypeDraft | null>(null);

  function makeDraft(): ContainerTypeDraft {
    const namesByLanguage: { [lang: string]: string } = {};
    Object.keys(UserLanguage).forEach((lang) => {
      namesByLanguage[lang] = '';
    });
    return {
      id: DRAFT_ID,
      name: '',
      internalName: '',
      namesByLanguage,
      class: 'ProductPortfolio',
      orderKey: 0,
      iconUrl: '',
      sizeHelper: '',
      imageUrl: '',
      countryCodes: [],
      hexCode: '',
      packingUnit: 0,
      groupId: parseInt(groupOptions[0]?.value ?? '1', 10),
      sku: '',
      branding: '',
      legalOwner: '',
      packingInstructions: '',
      cleanupNote: '',
      weight: 0,
      archived: false,
      segment: null,
      pricesByCurrency: deepClone(defaultNewContainerType.pricesByCurrency),
    };
  }

  const openDraft = () => setDraft(makeDraft());
  const closeDraft = () => setDraft(null);

  function updateDraftField(attrName: string, attrValue: unknown) {
    setDraft((prev) => {
      if (prev == null) return prev;
      const next: any = {
        ...prev,
        namesByLanguage: { ...prev.namesByLanguage },
      };
      if (attrName === 'namesByLanguage' || attrName === 'name') {
        if (language === UserLanguage.En) {
          Object.keys(next.namesByLanguage).forEach(
            (l) => (next.namesByLanguage[l] = attrValue),
          );
        } else {
          next.namesByLanguage[language] = attrValue;
        }
        next.name = next.namesByLanguage[UserLanguage.En] ?? '';
      } else if (attrName === 'group') {
        next.groupId = parseInt(attrValue as string, 10);
      } else {
        next[attrName] = attrValue;
      }
      return next;
    });
  }

  /** Prices for a currency, expanded from EUR via the static multipliers. */
  function getDraftPrices(currency: string): NewContainerPrice {
    if (draft == null) {
      return { ...defaultNewContainerType.pricesByCurrency.EUR };
    }
    return (
      draft.pricesByCurrency[currency] ??
      expandDraftPrices(draft.pricesByCurrency[DEFAULT_CURRENCY], currency)
    );
  }

  function setDraftPrice(currency: string, draftKey: string, raw: string) {
    setDraft((prev) => {
      if (prev == null) return prev;
      const current =
        prev.pricesByCurrency[currency] ??
        expandDraftPrices(prev.pricesByCurrency[DEFAULT_CURRENCY], currency);
      return {
        ...prev,
        pricesByCurrency: {
          ...prev.pricesByCurrency,
          [currency]: { ...current, [draftKey]: raw.length > 0 ? raw : '0' },
        },
      };
    });
  }

  const canCreateDraft =
    draft != null && !isNullOrWhitespace(draft.name) && draft.segment != null;

  return {
    draft,
    canCreateDraft,
    openDraft,
    closeDraft,
    updateDraftField,
    getDraftPrices,
    setDraftPrice,
  };
}
